import { useCallback, useEffect, useState } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import LottoShell from "../components/lotto/LottoShell";
import ResultsStrip from "../components/lotto/ResultsStrip";
import LottoHero from "../components/lotto/LottoHero";
import LastBuyTicket from "../components/dashboard/LastBuyTicket";
import { GAME_ROOMS } from "../utils/games";
import BannerSlider from "../components/lotto/BannerSlider";
import { useCustomColors } from "../hooks/useCustomColors";
import {
  CurrentRound,
  GameResult,
  LAST_BUY_TICKET_QUERY_KEY,
  getCurrentRound,
  getLastBuyTicket,
  getRecentResults,
  getGames,
  GameHallItem,
  ApiDraw,
  ApiTicket,
  toCurrentRound,
  toGameResult,
  toTicket,
  shouldShowLastBuyTicket,
} from "../api";
import { useTranslation } from "react-i18next";
import { RealtimeEvent, usePlayerLive, usePlayerRealtime } from "../hooks/usePlayerRealtime";
import { useDrawCountdown } from "../hooks/useDrawCountdown";
import GameHall from "../components/dashboard/GameHall";

const DashboardPage = () => {
  const colors = useCustomColors();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [round, setRound] = useState<CurrentRound | null>(null);
  const [results, setResults] = useState<GameResult[]>([]);
  const [games, setGames] = useState<GameHallItem[]>(GAME_ROOMS);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const load = useCallback(async () => {
    const [roundResponse, resultsResponse, gamesResponse] = await Promise.all([
      getCurrentRound(),
      getRecentResults({ page: 1, limit: 6 }),
      getGames(),
    ]);
    if (roundResponse.status === 1) setRound(roundResponse.data);
    if (resultsResponse.status === 1) setResults(resultsResponse.data);
    if (gamesResponse.status === 1) setGames(gamesResponse.items);
  }, []);

  // The socket moves the round on; the clocks come off the round's own
  // timestamps, so its second counts are deliberately not read here.
  const handleRealtimeEvent = useCallback((event: RealtimeEvent) => {
    const data = event.data || {};

    if (event.event === "draw.tick") {
      setRound((previous) =>
        previous
          ? {
              ...previous,
              draw_no: typeof data.drawNo === "number" ? data.drawNo : previous.draw_no,
              period_id: data.periodCode || previous.period_id,
              status:
                typeof data.isBettingOpen === "boolean"
                  ? data.isBettingOpen
                    ? "OPEN"
                    : "LOCKED"
                  : previous.status,
              closes_at: data.betsCloseAt || previous.closes_at,
              draw_at: data.closesAt || previous.draw_at,
            }
          : previous
      );
      return;
    }

    if (event.event === "draw.lockdown" || event.event === "draw.locked") {
      setRound((previous) => (previous ? { ...previous, status: "LOCKED" } : previous));
    }
  }, []);

  usePlayerRealtime(handleRealtimeEvent);

  usePlayerLive<ApiDraw>("current-draw", {}, (draw) => {
    setRound((previous) =>
      previous && typeof previous.draw_no === "number" && draw.drawNo < previous.draw_no
        ? previous
        : toCurrentRound(draw)
    );
  });

  usePlayerLive<{ items: ApiDraw[] }>("recent-draws", { limit: 6 }, (response) => {
    setResults(response.items.map(toGameResult));
  });

  usePlayerLive<ApiTicket>("ticket", {}, (ticket) => {
    queryClient.setQueryData(LAST_BUY_TICKET_QUERY_KEY, {
      status: 1,
      system: "bets",
      message: "Last ticket retrieved",
      data: toTicket(ticket),
    });
  });

  useEffect(() => {
    const run = async () => {
      setIsLoading(true);
      try {
        await load();
      } catch (error) {
        console.error("Dashboard load error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [load]);

  const { betSeconds, drawSeconds } = useDrawCountdown(round, load);

  // Backend owns visibility via data.is_show; a failed request simply leaves
  // the card hidden and the hero full width.
  const { data: lastBuyTicket, isLoading: isTicketResolving } = useQuery({
    queryKey: LAST_BUY_TICKET_QUERY_KEY,
    queryFn: getLastBuyTicket,
    retry: false,
  });

  const ticketData = lastBuyTicket?.data;
  const showLastBuyTicket = shouldShowLastBuyTicket(ticketData);
  // Hold the second column while the flag resolves, so the grid never snaps
  // from full-width to half-width once the response lands.
  const reserveTicketColumn = isTicketResolving || showLastBuyTicket;

  const isOpen = round?.status === "OPEN" && betSeconds > 0;
  return (
    <LottoShell>
      <Box sx={{ mt: "var(--home-section-gap)" }}>
        <BannerSlider />
      </Box>
      <Box
        sx={{
          mt: "var(--home-section-gap)",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: reserveTicketColumn ? "minmax(0, 2fr) minmax(320px, 1fr)" : "1fr",
          },
          alignItems: { xs: "start", lg: "stretch" },
          gap: { xs: "16px", md: "20px" },
        }}
      >
        <LottoHero
          round={round}
          remaining={drawSeconds}
          isOpen={isOpen}
          onPlay={() => navigate("/game/bet")}
        />

        {isTicketResolving ? (
          <Skeleton
            variant="rounded"
            sx={{
              borderRadius: "24px",
              minHeight: { xs: "260px", md: "380px" },
              bgcolor: colors.background.panelSecondary,
            }}
          />
        ) : showLastBuyTicket && ticketData?.ticket ? (
          <LastBuyTicket ticket={ticketData.ticket} />
        ) : null}
      </Box>

      <GameHall games={games} />

      <Box sx={{ mt: "var(--home-section-gap)", p: { xs: "16px", md: "24px" }, borderRadius: "20px", background: colors.card.gradient, border: `1px solid ${colors.border.accent}`, boxShadow: colors.card.shadow }}>
        <Typography sx={{ fontFamily: "Montserrat, sans-serif", fontSize: { xs: "18px", md: "20px" }, fontWeight: 800, fontStyle: "italic", color: colors.heading.gold }}>
          {t("dashboard_recent_results")}
        </Typography>
        <Box sx={{ mt: "18px" }}>
          {isLoading ? <Skeleton variant="rounded" height={110} sx={{ bgcolor: colors.background.panelSecondary }} /> : <ResultsStrip results={results.slice(0, 6)} />}
        </Box>
      </Box>

    </LottoShell>
  );
};

export default DashboardPage;
